import { logError } from '../utils/errorLogger';
import BaseApi from './BaseApi';
import { unwrapPaginatedResponse, buildPaginationParams } from '../utils/paginatedApi';
class HotelClientApi extends BaseApi {
  constructor() {
    super('client'); // Set the endpoint to "client"
  }

  // Get all clients
  async getAllClients() {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async getClientsPage({ page = 1, limit = 10, filters = {} } = {}) {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}`, {
        params: {
          ...buildPaginationParams({ page, limit }),
          ...filters,
        },
      });
      return unwrapPaginatedResponse(response.data);
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Get a single client by ID
  async getClientById(id) {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}/${id}`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Create a new client
  async createClient(clientData) {
    try {
      const response = await this.getApiInstance(true).post(`/${this.endpoint}`, clientData);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Update a client
  async updateClient(id, clientData) {
    try {
      const response = await this.getApiInstance(true).put(`/${this.endpoint}/${id}`, clientData);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Delete a client
  async deleteClient(id) {
    try {
      const response = await this.getApiInstance(true).delete(`/${this.endpoint}/${id}`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Search clients
  async searchClients(query) {
    try {
      const response = await this.getApiInstance(true).get(
        `/${this.endpoint}/search?query=${encodeURIComponent(query)}`
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Get clients by type (personne_physique or personne_moral)
  async getClientsByType(type) {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}/type/${type}`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async getAllPays() {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}/pays`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Get all fournisseur types for nature field
  async getFournisseurTypes() {
    try {
      const response = await this.getApiInstance(true).get(
        '/fournisseur_configuration/type_fournisseur'
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Get all clients for dropdown selection
  async getAllClientsForDropdown() {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}/dropdown`);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Create a new B2B client using dedicated endpoint
  async createB2BClient(clientData) {
    try {
      // Use the dedicated B2B endpoint that handles all field defaults
      const response = await this.getApiInstance(true).post(`/${this.endpoint}/b2b`, clientData);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Find client by phone number (for physique clients)
  async findClientByPhone(phone) {
    try {
      const response = await this.getApiInstance(true).get(
        `/${this.endpoint}/find/by-phone?phone=${encodeURIComponent(phone)}`
      );
      return response.data;
    } catch (error) {
      // Return null if client not found (404), throw for other errors
      if (error?.response?.status === 404) {
        return null;
      }
      logError(error);
      throw error;
    }
  }

  // Find client by RNE (matricule fiscal) (for morale clients)
  async findClientByRNE(rne) {
    try {
      const response = await this.getApiInstance(true).get(
        `/${this.endpoint}/find/by-rne?rne=${encodeURIComponent(rne)}`
      );
      return response.data;
    } catch (error) {
      // Return null if client not found (404), throw for other errors
      if (error?.response?.status === 404) {
        return null;
      }
      logError(error);
      throw error;
    }
  }

  // Create physique (individual) client
  async createPhysiqueClient(clientData) {
    try {
      // Use /clients/physique endpoint (clients table without 's') instead of /client/physique
      const response = await this.getApiInstance(true).post(
        `/clients/physique`,
        clientData
      );
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Create morale (company) client
  async createMoraleClient(clientData) {
    try {
      // Use /clients/morale endpoint (clients table without 's') instead of /client/morale
      const response = await this.getApiInstance(true).post(`/clients/morale`, clientData);
      return response.data;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Get client by partenaire ID
  async getClientByPartenaireId(partenaireId) {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}/by-partenaire/${partenaireId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null; // Client not found for this partenaire
      }
      logError(error);
      throw error;
    }
  }
}

export default new HotelClientApi();
